import { useState } from 'react';
import { Input, Switch, Textarea } from '@coordio/ui';
import type { AgentParameterDto } from '../../types/agent';

interface ParameterInputProps {
  parameter: AgentParameterDto;
  value: any;
  onChange: (value: any) => void;
  error?: string;
}

/**
 * Component for inputting a single parameter value based on its type.
 */
export const ParameterInput = ({ parameter, value, onChange, error }: ParameterInputProps) => {
  const [jsonError, setJsonError] = useState<string | null>(null);

  const handleChange = (newValue: any) => {
    onChange(newValue);
    setJsonError(null);
  };

  const handleJsonChange = (jsonString: string) => {
    try {
      if (jsonString.trim()) {
        JSON.parse(jsonString);
        setJsonError(null);
      }
      handleChange(jsonString);
    } catch (e) {
      setJsonError('Invalid JSON');
      handleChange(jsonString);
    }
  };

  const fieldWrapper = (label: string, required: boolean, helperText: string | undefined, hasError: boolean, children: React.ReactNode) => (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
      {hasError && error && <p className="text-xs text-red-500">{error}</p>}
      {helperText && !hasError && <p className="text-xs text-[var(--color-text-secondary)]">{helperText}</p>}
    </div>
  );

  const renderInput = () => {
    if (parameter.isSecret) {
      return fieldWrapper(parameter.label, false,
        parameter.hasSecretConfigured
          ? 'Configured in Azure Key Vault and used for this session.'
          : 'No secret is configured in Azure Key Vault.',
        !!error,
        <Input className="w-full" value={parameter.hasSecretConfigured ? '••••••••' : ''} disabled type="password" />
      );
    }

    switch (parameter.type.toLowerCase()) {
      case 'string': {
        const isMultiline = parameter.key.includes('prompt') || parameter.key.includes('template');
        return fieldWrapper(parameter.label, parameter.isRequired, parameter.description, !!error,
          isMultiline ? (
            <Textarea className="w-full" value={value || ''} onChange={(e) => handleChange(e.target.value)} required={parameter.isRequired} rows={4} />
          ) : (
            <Input className="w-full" value={value || ''} onChange={(e) => handleChange(e.target.value)} required={parameter.isRequired} type="text" />
          )
        );
      }

      case 'number':
      case 'int':
      case 'integer':
        return fieldWrapper(parameter.label, parameter.isRequired, parameter.description, !!error,
          <Input className="w-full" type="number" value={value || ''} onChange={(e) => handleChange(Number(e.target.value))} required={parameter.isRequired} />
        );

      case 'double':
      case 'float':
      case 'decimal':
        return fieldWrapper(parameter.label, parameter.isRequired, parameter.description, !!error,
          <Input className="w-full" type="number" step="any" value={value || ''} onChange={(e) => handleChange(Number(e.target.value))} required={parameter.isRequired} />
        );

      case 'date':
        return fieldWrapper(parameter.label, parameter.isRequired, parameter.description, !!error,
          <Input className="w-full" type="date" value={value || ''} onChange={(e) => handleChange(e.target.value)} required={parameter.isRequired} />
        );

      case 'boolean':
      case 'bool':
        return (
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <Switch checked={value || false} onCheckedChange={(checked) => handleChange(checked)} />
            {parameter.label}
          </label>
        );

      case 'json':
      case 'object':
        return fieldWrapper(parameter.label, parameter.isRequired,
          error || jsonError || parameter.description || 'Enter valid JSON',
          !!error || !!jsonError,
          <Textarea className="w-full font-mono" rows={6} value={value || ''} onChange={(e) => handleJsonChange(e.target.value)} required={parameter.isRequired} />
        );

      case 'array':
        return fieldWrapper(parameter.label, parameter.isRequired,
          error || jsonError || parameter.description || 'Enter valid JSON array',
          !!error || !!jsonError,
          <Textarea className="w-full font-mono" rows={4} value={value || ''} onChange={(e) => handleJsonChange(e.target.value)} required={parameter.isRequired} />
        );

      default:
        return fieldWrapper(parameter.label, parameter.isRequired, parameter.description, !!error,
          <Input className="w-full" value={value || ''} onChange={(e) => handleChange(e.target.value)} required={parameter.isRequired} />
        );
    }
  };

  return (
    <div className="mb-4">
      {renderInput()}
      {!parameter.isSecret && parameter.defaultValue && !value && (
        <span className="text-xs text-muted-foreground mt-1 block">
          Default: {parameter.defaultValue}
        </span>
      )}
    </div>
  );
};
